/**
 * Comprehensive validation utilities for form fields
 * Provides real-time validation to prevent database errors
 */

export const ValidationRules = {
  // Email validation - RFC 5322 compliant
  email: {
    pattern: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
    message: 'Ungültige E-Mail-Adresse',
    validate: (value) => ValidationRules?.email?.pattern?.test(value?.trim())
  },

  // German postal code (5 digits)
  postalCode: {
    pattern: /^\d{5}$/,
    message: 'PLZ muss 5 Ziffern enthalten',
    validate: (value) => ValidationRules?.postalCode?.pattern?.test(value?.trim())
  },

  // German phone number (flexible format)
  phone: {
    pattern: /^[\+]?[(]?[0-9]{2,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/,
    message: 'Ungültige Telefonnummer',
    validate: (value) => {
      if (!value?.trim()) return true; // Optional field
      return ValidationRules?.phone?.pattern?.test(value?.replace(/\s/g, ''));
    }
  },

  // URL validation with protocol requirement
  url: {
    pattern: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
    message: 'URL muss mit http:// oder https:// beginnen',
    validate: (value) => {
      if (!value?.trim()) return true; // Optional field
      try {
        new URL(value?.trim());
        return ValidationRules?.url?.pattern?.test(value?.trim());
      } catch {
        return false;
      }
    }
  },

  // Hex color validation
  hexColor: {
    pattern: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
    message: 'Farbe muss im Hex-Format sein (z.B. #FF5733)',
    validate: (value) => {
      if (!value?.trim()) return true; // Optional field
      return ValidationRules?.hexColor?.pattern?.test(value?.trim());
    }
  },

  // Required field validation
  required: {
    validate: (value) => {
      if (typeof value === 'string') {
        return value?.trim()?.length > 0;
      }
      return value != null && value !== '';
    },
    message: 'Dieses Feld ist erforderlich'
  },

  // Minimum length validation
  minLength: (min) => ({
    validate: (value) => !value || value?.length >= min,
    message: `Mindestens ${min} Zeichen erforderlich`
  }),

  // Maximum length validation
  maxLength: (max) => ({
    validate: (value) => !value || value?.length <= max,
    message: `Maximal ${max} Zeichen erlaubt`
  }),

  // Date validation (not in future, minimum age)
  birthdate: {
    validate: (value) => {
      if (!value) return false;
      const date = new Date(value);
      const today = new Date();
      const age = today?.getFullYear() - date?.getFullYear();
      return date <= today && age >= 16 && age <= 120;
    },
    message: 'Ungültiges Geburtsdatum (mindestens 16 Jahre alt)'
  },

  // Chassis/VIN number validation (17 characters, alphanumeric)
  chassisNumber: {
    pattern: /^[A-HJ-NPR-Z0-9]{17}$/,
    message: 'FIN muss 17 Zeichen enthalten (ohne I, O, Q)',
    validate: (value) => {
      const cleaned = value?.trim()?.toUpperCase();
      return ValidationRules?.chassisNumber?.pattern?.test(cleaned);
    }
  },

  // German license plate validation
  licensePlate: (value) => {
    if (!value || !value?.trim()) {
      return 'Kennzeichen ist erforderlich';
    }
    // German license plate format validation
    // Typical formats: AB-CD 1234, ABC-DE 123, A-BC 1234E, etc.
    // Pattern: 1-3 letters, dash, 1-2 letters, space, 1-4 alphanumeric
    const germanLicensePlatePattern = /^[A-ZÄÖÜ]{1,3}-[A-ZÄÖÜ]{1,2}\s?[0-9]{1,4}[EH]?$/i;

    const cleaned = value?.trim()?.toUpperCase();
    if (!germanLicensePlatePattern?.test(cleaned)) {
      return 'Bitte geben Sie ein gültiges deutsches Kennzeichen ein (z.B. M-AB 1234)';
    }
    return null;
  }
};

/**
 * Real-time field validator
 * @param {string} fieldName - Name of the field
 * @param {any} value - Value to validate
 * @param {object} rules - Validation rules to apply
 * @returns {string|null} Error message or null if valid
 */
export const validateField = (fieldName, value, rules = []) => {
  for (const rule of rules) {
    if (typeof rule === 'function') {
      const result = rule(value);
      if (result !== true) {
        return result; // Return error message
      }
    } else if (rule?.validate) {
      if (!rule?.validate(value)) {
        return rule?.message;
      }
    }
  }
  return null; // No errors
};

/**
 * Validate entire form object
 * @param {object} formData - Form data to validate
 * @param {object} validationSchema - Schema defining validation rules per field
 * @returns {object} Object with field names as keys and error messages as values
 */
export const validateForm = (formData, validationSchema) => {
  const errors = {};
  
  Object.keys(validationSchema)?.forEach(fieldName => {
    const rules = validationSchema?.[fieldName];
    const value = formData?.[fieldName];
    const error = validateField(fieldName, value, rules);
    
    if (error) {
      errors[fieldName] = error;
    }
  });
  
  return errors;
};

/**
 * Validation schema for dealership form
 */
export const dealershipValidationSchema = {
  name: [
    ValidationRules?.required,
    ValidationRules?.minLength(2),
    ValidationRules?.maxLength(100)
  ],
  city: [
    ValidationRules?.required,
    ValidationRules?.minLength(2),
    ValidationRules?.maxLength(50)
  ],
  street: [
    ValidationRules?.minLength(3),
    ValidationRules?.maxLength(100)
  ],
  postalCode: [
    ValidationRules?.postalCode
  ],
  email: [
    ValidationRules?.required,
    ValidationRules?.email
  ],
  phone: [
    ValidationRules?.required,
    ValidationRules?.phone
  ],
  contactPerson: [
    ValidationRules?.minLength(2),
    ValidationRules?.maxLength(100)
  ],
  website: [
    ValidationRules?.required,
    ValidationRules?.url
  ],
  ciPrimaryColor: [
    ValidationRules?.hexColor
  ],
  ciSecondaryColor: [
    ValidationRules?.hexColor
  ],
  logo: [
    ValidationRules?.required
  ]
};

/**
 * Validation schema for customer registration form
 */
export const registrationValidationSchema = {
  firstname: [
    ValidationRules?.required,
    ValidationRules?.minLength(2),
    ValidationRules?.maxLength(50)
  ],
  lastname: [
    ValidationRules?.required,
    ValidationRules?.minLength(2),
    ValidationRules?.maxLength(50)
  ],
  street: [
    ValidationRules?.required,
    ValidationRules?.minLength(3),
    ValidationRules?.maxLength(100)
  ],
  postalCode: [
    ValidationRules?.required,
    ValidationRules?.postalCode
  ],
  city: [
    ValidationRules?.required,
    ValidationRules?.minLength(2),
    ValidationRules?.maxLength(50)
  ],
  birthdate: [
    ValidationRules?.required,
    ValidationRules?.birthdate
  ],
  email: [
    ValidationRules?.required,
    ValidationRules?.email
  ],
  phone: [
    ValidationRules?.phone
  ],
  licensePlate: [
    ValidationRules?.licensePlate
  ]
};

/**
 * Validation schema for voucher code
 */
export const voucherValidationSchema = {
  voucherCode: [
    ValidationRules?.required,
    {
      validate: (value) => {
        const cleaned = value?.replace(/[^A-Z0-9]/g, '');
        return cleaned?.length === 12; // XXXX-XXXX-XXXX format
      },
      message: 'Gutscheincode muss 12 Zeichen enthalten'
    }
  ]
};

/**
 * Sanitize input to prevent XSS and injection attacks
 * @param {string} input - User input to sanitize
 * @returns {string} Sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    ?.trim()
    ?.replace(/[<>]/g, '') // Remove angle brackets
    ?.replace(/javascript:/gi, '') // Remove javascript: protocol
    ?.replace(/on\w+\s*=/gi, ''); // Remove event handlers
};

/**
 * Sanitize dealership name input to allow spaces while preventing XSS
 * @param {string} input - Dealership name input
 * @returns {string} Sanitized name with spaces preserved
 */
export const sanitizeDealershipName = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    ?.replace(/[<>]/g, '') // Remove angle brackets
    ?.replace(/javascript:/gi, '') // Remove javascript: protocol
    ?.replace(/on\w+\s*=/gi, ''); // Remove event handlers (but keep spaces)
};

/**
 * Sanitize address input (street, house number) to preserve spaces
 * This function explicitly preserves all spaces for proper address formatting
 * @param {string} input - Address input
 * @returns {string} Sanitized address with all spaces preserved
 */
export const sanitizeAddress = (input) => {
  if (typeof input !== 'string') return input;
  if (input === '') return input;
  
  // Only remove dangerous characters, explicitly preserve spaces
  let sanitized = input;
  sanitized = sanitized?.replace(/[<>]/g, ''); // Remove angle brackets
  sanitized = sanitized?.replace(/javascript:/gi, ''); // Remove javascript: protocol  
  sanitized = sanitized?.replace(/on\w+=/gi, ''); // Remove event handlers without consuming spaces
  
  return sanitized;
};

/**
 * Format and validate phone number
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  const cleaned = phone?.replace(/\D/g, '');
  
  if (cleaned?.startsWith('49')) {
    return `+${cleaned?.slice(0, 2)} ${cleaned?.slice(2, 5)} ${cleaned?.slice(5)}`;
  }
  
  if (cleaned?.startsWith('0')) {
    return `${cleaned?.slice(0, 4)} ${cleaned?.slice(4)}`;
  }
  
  return phone;
};

export default {
  ValidationRules,
  validateField,
  validateForm,
  dealershipValidationSchema,
  registrationValidationSchema,
  voucherValidationSchema,
  sanitizeInput,
  sanitizeDealershipName,
  sanitizeAddress,
  formatPhoneNumber
};